export const getDownloadColumns = ({ exclude = [], append = [] } = {}) => [
  'address',
  'all_sqft',
  'baths',
  'beds',
  'city',
  'count',
  'county',
  'delq_balance',
  'ed_ba_plus',
  'hh_w_kids',
  'is_vacant',
  'land_use',
  'med_hh_inc',
  'med_rent',
  'mortgage_foreclosure_1y',
  'mortgage_foreclosure_5y',
  'mortgage_foreclosure_hex',
  'mortgage_foreclosure',
  'occupied_1y',
  'occupied_5y',
  'occupied',
  'owner_name',
  'owner_name',
  'owner_occ_1y',
  'owner_occ_5y',
  'owner_occ',
  'owner_occupancy',
  'parcel_id',
  'pov_pct',
  'rc_2p',
  'rc_afam',
  'rc_asian',
  'rc_islnd',
  'rc_natv',
  'rc_other',
  'rc_white',
  'reoccupancy_detector',
  'res_sp_sqft_1y',
  'res_sp_sqft_5y',
  'res_sp_sqft',
  'residential_area',
  'structure',
  'tax_delinquent_1y',
  'tax_delinquent_5y',
  'tax_delinquent',
  'tax_foreclosure_1y',
  'tax_foreclosure_5y',
  'tax_foreclosure_hex',
  'tax_foreclosure',
  'tax_status',
  'total_balance',
  'total_owed',
  'total_paid',
  'vacancy_detector',
  'vacant_lot_1y',
  'vacant_lot_5y',
  'vacant_lot',
  'year_built',
  'zip',
  'zoning',
  'general_zoning',
]
  .filter((key) => !exclude.includes(key))
  .concat(append);

export const getReportHeader = ({ exclude = [], append = [], hideActions = false } = {}) => [
  {
    column: 'parcel_id',
    type: 'HIDDEN',
  },
  {
    column: 'geom',
    padding: 100,
    type: 'MAP',
  },
  {
    type: 'ACTIONS',
    options: hideActions ? [] : ['PRINT', 'CSV', 'SHAPEFILE'],
    props: getDownloadColumns({
      exclude,
      append,
    }),
  },
  {
    label: 'Number of Selected Properties',
    column: 'parcel_id+count',
    type: 'TEXT',
    method: 'count',
  },
];

export const salesIndicators = ({ after = 2008 } = {}) => [
  {
    label: 'Sales Indicators',
    type: 'TITLE',
  },
  {
    label: 'Average Residential Sale Price',
    title: 'Average Residential Sale Price',
    graph: 'avg_transactions_prc',
    type: 'LINE_GRAPH',
    after,
    useRatio: false,
    unit: 'Price per Square Foot',
    compare: [
      {
        to: 'CITY',
        label: 'Average Sale Price for the city',
      },
    ],
  },
  {
    label: 'Residential Sales Count',
    title: 'Residential Sales Count',
    graph: 'transactions_count',
    type: 'LINE_GRAPH',
    after,
    useRatio: false,
    unit: 'Number of Sales',
    renameColumn: 'transactions_count',
    compare: [
      {
        to: 'CITY',
        label: 'Sales Count for the city',
      },
    ],
  },
  {
    label: 'Average Commercial Sale Price',
    title: 'Average Commercial Sale Price',
    graph: 'avg_transactions_prc_commercial',
    type: 'LINE_GRAPH',
    after,
    useRatio: false,
    unit: 'Price per Square Foot',
    compare: [
      {
        to: 'CITY',
        label: 'Average Sale Price for the city',
      },
    ],
  },
  {
    label: 'Commercial Sales Count',
    title: 'Commercial Sales Count',
    graph: 'transactions_count_commercial',
    type: 'LINE_GRAPH',
    after,
    useRatio: false,
    unit: 'Number of Sales',
    renameColumn: 'transactions_count',
    compare: [
      {
        to: 'CITY',
        label: 'Sales Count for the city',
      },
    ],
  },
];

export const census = () => [
  {
    label: 'Average Socio-Economics & Demographics',
    type: 'TITLE',
  },
  {
    label: 'Source: 2020 U.S. Census',
    type: 'SUBTITLE',
  },
  {
    label: 'Census Comparison',
    type: 'CENSUS_COMPARISON_TABLE',
    table: 'census_table',
  },
];

export const censusCounty = () => [
  {
    label: 'Average Socio-Economics & Demographics',
    type: 'TITLE',
  },
  {
    label: 'Census Comparison',
    type: 'CENSUS_COMPARISON_TABLE',
    table: 'county_census_table',
  },
];

export const submarketCounts = () => [
  {
    label: 'Submarket Distribution',
    type: 'TITLE',
  },
  {
    label: 'Submarket Counts',
    type: 'CENSUS_COMPARISON_TABLE',
    table: 'submarket_count',
  },
];

export const heatmap = ({ exclude = [] } = {}) => [
  {
    label: 'Average Real Estate & Property Conditions and Trends',
    type: 'TITLE',
  },
  {
    label: 'Area Residential Price Per Sq. Foot',
    column: 'res_sp_sqft',
    type: 'DOLLARS',
    method: 'avg',
  },
  {
    label: 'Area Building Density (sq. ft. per hexagon)',
    column: 'all_sqft',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Density (parcels per hexagon)',
    column: 'count',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Percent Vacant Lots',
    column: 'vacant_lot',
    type: 'PERCENT',
    method: 'avg',
  },
  {
    label: 'Area Percent Tax Delinquent',
    column: 'tax_delinquent',
    type: 'PERCENT',
    method: 'avg',
  },
  {
    label: 'Area Percent Tax Foreclosure',
    column: 'tax_foreclosure_hex',
    type: 'PERCENT',
    method: 'avg',
  },
  {
    label: 'Area Percent Mortgage Foreclosure',
    column: 'mortgage_foreclosure_hex',
    type: 'PERCENT',
    method: 'avg',
  },
  {
    label: 'Area Percent Owner Occupied',
    column: 'owner_occ',
    type: 'PERCENT',
    method: 'avg',
  },
  {
    label: 'Area Percent Occupied',
    column: 'occupied',
    type: 'PERCENT',
    method: 'avg',
  },
  {
    label: 'Area Change in Residential Price Per Sq. Foot - 1 year',
    column: 'res_sp_sqft_1y',
    type: 'PERCENT',
    method: 'avg',
  },
  {
    label: 'Area Change in Residential Price Per Sq. Foot - 5 year',
    column: 'res_sp_sqft_5y',
    type: 'PERCENT',
    method: 'avg',
  },
  {
    label: 'Area Change in Tax Delinquency - 1 year',
    column: 'tax_delinquent_1y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Tax Delinquency - 5 year',
    column: 'tax_delinquent_5y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Tax Foreclosure - 1 year',
    column: 'tax_foreclosure_1y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Tax Foreclosure - 5 year',
    column: 'tax_foreclosure_5y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Mortgage Foreclosure - 1 year',
    column: 'mortgage_foreclosure_1y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Mortgage Foreclosure - 5 year',
    column: 'mortgage_foreclosure_5y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Occupancy - 1 year',
    column: 'occupied_1y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Occupancy - 5 year',
    column: 'occupied_5y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Owner Occupancy - 1 year',
    column: 'owner_occ_1y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Owner Occupancy - 5 year',
    column: 'owner_occ_5y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Vacant Lot - 1 year',
    column: 'vacant_lot_1y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'Area Change in Vacant Lot - 5 year',
    column: 'vacant_lot_5y',
    type: 'NUMBER_DECIMAL',
    method: 'avg',
  },
  {
    label: 'The section above shows averages of the aggregate hexagons that contain the selected parcels.',
    type: 'SUBTITLE',
  },
].filter(({ column }) => !exclude.includes(column));

export const impact = ({ exclude = [] } = {}) => [
  {
    label: 'Rehabilition Impact',
    type: 'TITLE',
  },
  {
    column: 'rehab_impact+rehab_count',
    label: 'Number of Rehabilitations in Selected Properties',
    type: 'TEXT',
    method: 'count',
  },
  {
    column: 'rehab_impact+sum',
    label: 'Total Impact of all Rehabilitations',
    type: 'DOLLARS',
    method: 'sum',
  },
  {
    column: 'rehab_impact',
    label: 'Average Impact per Rehabilitation',
    default: 'N/A',
    type: 'DOLLARS',
    method: 'avg',
  },
  {
    column: 'rehab_predict_impact',
    label: 'Average Impact per Rehabilitation (Predicted)',
    default: 'N/A',
    type: 'DOLLARS',
    method: 'avg',
  },
  {
    label: 'Demolition Impact',
    type: 'TITLE',
  },
  {
    column: 'demo_impact+demo_count',
    label: 'Number of Demolitions in Selected Properties',
    type: 'TEXT',
    method: 'count',
  },

  {
    column: 'demo_impact+sum',
    label: 'Total Impact of all Demolitions',
    type: 'DOLLARS',
    method: 'sum',
  },
  {
    column: 'demo_impact',
    label: 'Average Impact per Demolition',
    default: 'N/A',
    type: 'DOLLARS',
    method: 'avg',
  },
  {
    column: 'demo_predict_impact',
    label: 'Average Impact per Demolition (Predicted)',
    default: 'N/A',
    type: 'DOLLARS',
    method: 'avg',
  },
].filter(({ column }) => !exclude.includes(column));

export const placeImpact = ({ exclude = [] } = {}) => [
  {
    label: 'Place Impact',
    type: 'TITLE',
  },

  {
    column: 'impact_total',
    label: 'Total Place Impact',
    default: 'N/A',
    type: 'DOLLARS',
    method: 'sum',
  },
  {
    column: 'total_investment',
    label: 'Total Investment',
    default: 'N/A',
    type: 'DOLLARS',
    method: 'sum',
  },
  {
    column: 'place-impact-bcr',
    label: 'Benefit Cost Ratio',
    default: 'N/A',
    input: ['sum:impact_total', 'sum:total_investment'],
    type: 'DOLLARS',
    postpend: ' for each $1 of investment',
    localMethod: 'divide',
  },
  {
    column: 'impact_total+avg',
    label: 'Average Place Impact',
    default: 'N/A',
    type: 'DOLLARS',
    method: 'avg',
  },
].filter(({ column }) => !exclude.includes(column));
